import {
    Star,
    Award,
    Gift,
    ChevronLeft,
    ChevronRight,
    Sparkles,
    Eye,
} from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { ProductCategory, ProductosIndex } from "@/types/app";
import { getImageUrl, separadorMiles } from "@/utils/global";
import { LazyImage } from "@/Components/LazyImage";
import ScrollReveal from "@/Components/ScrollReveal";

const ProductosDestacados = ({
    AgregadosRecientemente,
    masVendidos,
    canjeables,
    destacados,
}: {
    AgregadosRecientemente: ProductosIndex[];
    masVendidos: ProductosIndex[];
    canjeables: ProductosIndex[];
    destacados: ProductCategory[];
}) => {
    // Crear tabs dinámicamente desde los datos
    const tabs = destacados.map((category) => ({
        id: category.subCategoryId.toString(),
        label: category.subCategory,
    }));

    // Componente Product simplificado para manejar ambos tipos de datos
    type ProductData =
        | ProductosIndex
        | {
            id: number;
            nombre: string;
            nombre_sabor?: string;
            precio?: number;
            imagen?: string;
            sabor?: string;
            es_individual?: number;
            listar_individual?: number;
        };

    const Product = ({
        data,
        type,
    }: {
        data: ProductData;
        type: "nuevo" | "normal" | "canjeable" | "masVendido";
    }) => {
        // Compatibilidad entre ambos formatos de datos
        const listarIndividual =
            "listar_individual" in data
                ? data.listar_individual
                : "es_individual" in data
                    ? data.es_individual
                    : 1;

        let url: string;

        if (type === "canjeable") {
            url = `${window.location.origin}/profile/mypoints`;
        } else {
            url =
                listarIndividual == 0
                    ? `/producto/${data.nombre}`
                    : `/producto/${data.nombre}/${data.sabor || ""}`;
        }

        let nombre: string =
            "nombre_sabor" in data && data.nombre_sabor
                ? data.nombre_sabor
                : type === "masVendido"
                    ? data.sabor || data.nombre
                    : data.nombre;

        const precio = "precio" in data ? data.precio : undefined;

        return (
            <div
                className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-gray-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer min-w-[280px] max-w-[280px] md:min-w-[300px] md:max-w-[300px] flex-shrink-0 relative group"
                style={type === "canjeable" ? {
                    backgroundImage: `url(${getImageUrl("/images/patrones/fondo%20test.png")})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                } : {}}
            >
                <a href={url} className="block">
                    <div className="aspect-square overflow-hidden relative">
                        <LazyImage
                            src={getImageUrl(data.imagen || "")}
                            alt={nombre}
                            className="group-hover:scale-105 transition-transform duration-300"
                            containerClassName="w-full h-full"
                        />
                        {type === "nuevo" && (
                            <div className="absolute top-3 left-3 bg-[#790546] text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-sm">
                                NUEVO
                            </div>
                        )}
                        {type === "canjeable" && (
                            <div className="absolute top-3 left-3 bg-white text-[#790546] px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 shadow-md border border-gray-100">
                                <Gift className="w-3 h-3" />
                            </div>
                        )}
                    </div>

                    <div className="px-5 py-5 text-center bg-white/90 backdrop-blur-sm">
                        <h3 className="font-semibold text-[#790546] text-base mb-3 leading-tight text-balance text-center uppercase whitespace-pre-line group-hover:text-[#6a0440] transition-colors duration-300">
                            {nombre}
                        </h3>
                        {precio && (
                            <span className="text-sm text-gray-500 group-hover:text-gray-700 transition-colors duration-300">
                                ${separadorMiles(precio)}
                            </span>
                        )}
                    </div>
                </a>

                {/* Botón Ver Detalles - visible con hover en la tarjeta pero con hover propio */}
                <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <a href={url} className="block">
                        <div className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg border border-white/30 flex items-center gap-1 hover:bg-white hover:shadow-xl transition-all duration-300">
                            <Eye className="w-3 h-3 text-[#790546]" />
                            <span className="text-[#790546] font-medium text-xs">
                                Ver Detalles
                            </span>
                        </div>
                    </a>
                </div>
            </div>
        );
    };

    const scrollRefRecientes = useRef<HTMLDivElement>(null);
    const scrollRefComprados = useRef<HTMLDivElement>(null);
    const scrollRefCanjeables = useRef<HTMLDivElement>(null);

    // Crear refs dinámicos para cada categoría
    const scrollRefsDestacados = useRef<Record<string, HTMLDivElement | null>>(
        {}
    );

    const scroll = (
        ref: React.RefObject<HTMLDivElement> | HTMLDivElement | null,
        direction: "left" | "right"
    ) => {
        const element = ref && "current" in ref ? ref.current : ref;
        if (element) {
            const isMobile = window.innerWidth < 768;
            const scrollAmount = isMobile ? 300 : 324;
            element.scrollBy({
                left: direction === "left" ? -scrollAmount : scrollAmount,
                behavior: "smooth",
            });
        }
    };

    const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? "");

    // Estado dinámico para shouldCenter
    const [shouldCenter, setShouldCenter] = useState<Record<string, boolean>>(
        {}
    );

    const checkShouldCenter = () => {
        const isMobile = window.innerWidth < 768;
        const cardWidth = isMobile ? 280 : 300;
        const gap = isMobile ? 20 : 24;
        const containerPadding = isMobile ? 32 : 32;
        const availableWidth = window.innerWidth - containerPadding;
        const itemsPerView = Math.floor(
            (availableWidth + gap) / (cardWidth + gap)
        );

        const newShouldCenter: Record<string, boolean> = {
            recientes: AgregadosRecientemente.length <= itemsPerView,
            comprados: masVendidos.length <= itemsPerView,
            canjeables: canjeables.length <= itemsPerView,
        };

        // Agregar dinámicamente las categorías destacadas
        destacados.forEach((category) => {
            newShouldCenter[category.subCategoryId.toString()] =
                category.products.length <= itemsPerView;
        });

        setShouldCenter(newShouldCenter);
    };

    useEffect(() => {
        checkShouldCenter();
        window.addEventListener("resize", checkShouldCenter);
        return () => window.removeEventListener("resize", checkShouldCenter);
    }, [
        AgregadosRecientemente.length,
        masVendidos.length,
        canjeables.length,
        destacados,
    ]);

    const ScrollableSection = ({
        children,
        scrollRef,
        title,
        subtitle,
        icon,
        centerContent = false,
    }: {
        children: React.ReactNode;
        scrollRef: React.RefObject<HTMLDivElement>;
        title: string;
        subtitle: string;
        icon?: React.ReactNode;
        centerContent?: boolean;
    }) => (
        <section className="relative">
            <div className="flex flex-col items-center text-center mb-8">
                <div className="flex items-center gap-3 mb-4">
                    {icon}
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight text-balance">
                        {title}
                    </h2>
                </div>
                <p className="text-gray-600 max-w-2xl text-sm md:text-base leading-relaxed px-6 text-balance">
                    {subtitle}
                </p>
                <div className="w-16 h-0.5 bg-[#790546] rounded-full mt-5"></div>
            </div>

            <div className="relative">
                <button
                    onClick={() => scroll(scrollRef, "left")}
                    className="absolute left-2 md:left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-[#790546] p-2.5 hover:p-3 rounded-full hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                >
                    <ChevronLeft className="w-4 h-4 hover:w-5 hover:h-5 transition-all duration-300" />
                </button>

                <button
                    onClick={() => scroll(scrollRef, "right")}
                    className="absolute right-2 md:right-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-[#790546] p-2.5 hover:p-3 rounded-full hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                >
                    <ChevronRight className="w-4 h-4 hover:w-5 hover:h-5 transition-all duration-300" />
                </button>

                <div
                    ref={scrollRef}
                    className={`flex gap-5 md:gap-6 overflow-x-auto scrollbar-hide pb-6 md:pb-4 scroll-smooth px-4 md:px-4 ${centerContent ? "justify-center" : ""
                        }`}
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                    {children}
                </div>
            </div>
        </section>
    );

    return (
        <div className=" min-h-screen py-8 md:py-12">
            <div className="w-full max-w-none px-4 md:px-6 lg:px-8 space-y-12 md:space-y-16">
                <ScrollReveal yOffset={40} duration={0.6} once={true}>
                    <ScrollableSection
                        scrollRef={scrollRefRecientes}
                        title="Productos Nuevos"
                        subtitle="Delicias frescas horneadas hoy, preparadas con los mejores ingredientes"
                        icon={
                            <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-[#790546]" />
                        }
                        centerContent={shouldCenter.recientes}
                    >
                        {AgregadosRecientemente.map((product) => (
                            <Product data={product} key={product.id} type="nuevo" />
                        ))}
                    </ScrollableSection>
                </ScrollReveal>

                <ScrollReveal yOffset={40} duration={0.6} once={true}>
                    <div className="bg-gradient-to-br from-[#790546]/5 to-[#790546]/10 -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 py-12 md:py-16 rounded-2xl border border-[#790546]/10">
                        <ScrollableSection
                            scrollRef={scrollRefComprados}
                            title="Más Comprados"
                            subtitle="Los favoritos de nuestros clientes, probados y aprobados por su sabor excepcional"
                            icon={
                                <Award className="w-6 h-6 md:w-8 md:h-8 text-[#790546]" />
                            }
                            centerContent={shouldCenter.comprados}
                        >
                            {masVendidos.map((product, index) => (
                                <Product
                                    data={product}
                                    key={product.id + "-" + index}
                                    type="masVendido"
                                />
                            ))}
                        </ScrollableSection>
                    </div>
                </ScrollReveal>

                <ScrollReveal yOffset={40} duration={0.6} once={true}>
                    <ScrollableSection
                        scrollRef={scrollRefCanjeables}
                        title="Productos que Podrías Canjear"
                        subtitle="Usa tus puntos de fidelidad para obtener estas deliciosas recompensas"
                        icon={
                            <Gift className="w-8 h-8 md:w-10 md:h-10 text-[#790546]" />
                        }
                        centerContent={shouldCenter.canjeables}
                    >
                        {canjeables.map((product) => (
                            <Product
                                data={product}
                                key={product.id}
                                type="canjeable"
                            />
                        ))}
                    </ScrollableSection>
                </ScrollReveal>

                <ScrollReveal yOffset={40} duration={0.6} once={true}>
                    <div className="bg-gradient-to-br from-[#790546]/5 to-[#790546]/10 -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 py-12 md:py-16 rounded-2xl border border-[#790546]/10">
                        <div className="flex flex-col items-center text-center mb-8">
                            <div className="flex items-center gap-3 mb-4">
                                <Star className="w-6 h-6 md:w-8 md:h-8 text-[#790546]" />
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight text-balance">
                                    Productos Destacados
                                </h2>
                            </div>
                            <p className="text-gray-600 max-w-2xl text-sm md:text-base leading-relaxed px-6 text-balance">
                                Descubre nuestras especialidades organizadas por
                                categorías únicas
                            </p>
                            <div className="w-16 h-0.5 bg-[#790546] rounded-full mt-5 mb-8"></div>

                            <div className="flex flex-wrap justify-center gap-3 mb-10">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border ${activeTab === tab.id
                                            ? "bg-[#790546] text-white shadow-md border-[#790546]"
                                            : "bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200 hover:border-gray-300"
                                            }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="relative">
                            <button
                                onClick={() =>
                                    scroll(
                                        scrollRefsDestacados.current[activeTab],
                                        "left"
                                    )
                                }
                                className="absolute left-2 md:left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-[#790546] p-2.5 hover:p-3 rounded-full hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                            >
                                <ChevronLeft className="w-4 h-4 hover:w-5 hover:h-5 transition-all duration-300" />
                            </button>

                            <button
                                onClick={() =>
                                    scroll(
                                        scrollRefsDestacados.current[activeTab],
                                        "right"
                                    )
                                }
                                className="absolute right-2 md:right-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 text-[#790546] p-2.5 hover:p-3 rounded-full hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                            >
                                <ChevronRight className="w-4 h-4 hover:w-5 hover:h-5 transition-all duration-300" />
                            </button>
                            <div className="relative">
                                {destacados.map(
                                    (category) =>
                                        activeTab ===
                                        category.subCategoryId.toString() && (
                                            <div
                                                key={category.subCategoryId}
                                                ref={(el) => {
                                                    scrollRefsDestacados.current[
                                                        category.subCategoryId.toString()
                                                    ] = el;
                                                }}
                                                className={`flex gap-5 md:gap-6 overflow-x-auto scrollbar-hide pb-6 md:pb-4 scroll-smooth px-4 md:px-4 ${shouldCenter[
                                                    category.subCategoryId.toString()
                                                ]
                                                    ? "justify-center"
                                                    : ""
                                                    }`}
                                                style={{
                                                    scrollbarWidth: "none",
                                                    msOverflowStyle: "none",
                                                }}
                                            >
                                                {category.products.map(
                                                    (product) => (
                                                        <Product
                                                            data={product}
                                                            key={`${product.id}-${product.sabor ||
                                                                product.nombre
                                                                }`}
                                                            type="normal"
                                                        />
                                                    )
                                                )}
                                            </div>
                                        )
                                )}
                            </div>
                        </div>
                    </div>
                </ScrollReveal>
            </div>
        </div>
    );
};

export default ProductosDestacados;
